/**
 * DbPensionsPage — Manage Defined Benefit / Guaranteed Pensions
 *
 * Lets Simon add, edit, and delete DB pensions (Bank of England pension,
 * employer schemes, annuities, etc.) via the profile API.
 *
 * These pensions flow directly into the drawdown optimiser because
 * db_pension_income_at_age() reads from the same table.  They consume
 * Personal Allowance and the basic-rate band before SIPP drawdown starts,
 * so the impact preview below shows exactly how much drawdown room they absorb.
 */
import { useEffect, useState } from 'react'
import {
  PiggyBank, Plus, Edit2, Trash2, Save, RefreshCw, AlertTriangle, Info,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import PensionsTabs from '@/components/ui/PensionsTabs'

// ── Types ─────────────────────────────────────────────────────────────────────

interface DbPension {
  id: number
  owner: string
  name: string
  annual_amount_gbp: number
  start_age: number
  indexation_pct: number
  spouse_fraction: number
  notes?: string
}

interface BlendSources {
  state_pension_gbp: number
  db_pension_gbp: number
  gia_cgt_free_gbp: number
  isa_gbp: number
  pcls_tax_free_gbp: number
  sipp_basic_rate_gbp: number
  sipp_higher_rate_gbp: number
  extra_gia_gbp: number
}

interface OptimiseResponse {
  /** null when no drawdown year falls inside years_to_project (e.g. a
   *  retirement age far beyond the window) — the backend returns
   *  `next((...), None)` there, so this must be guarded before use. */
  first_retire_year: {
    gross_received_gbp: number
    net_received_gbp: number
    total_tax_gbp: number
    effective_tax_rate_pct: number
    sources: BlendSources
  } | null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const OWNER_BADGE: Record<string, string> = {
  Simon: 'badge-blue',
  Unman: 'badge-purple',
  Joint: 'badge-green',
}

interface PensionForm {
  owner: string
  name: string
  annual_amount_gbp: number | string
  start_age: number
  indexation_pct: number
  spouse_fraction: number
  notes: string
}

const BLANK_FORM: PensionForm = {
  owner: 'Simon',
  name: '',
  annual_amount_gbp: '',
  start_age: 65,
  indexation_pct: 2.5,
  spouse_fraction: 0.5,
  notes: '',
}

const OPTIMISE_DEFAULTS = {
  target_gbp: 200000,
  current_age: 57,
  retirement_age: 60,
  state_pension_age: 67,
  joint: true,
  years_to_project: 35,
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DbPensionsPage() {
  const [pensions, setPensions]     = useState<DbPension[]>([])
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState<string | null>(null)

  // Form state
  const [form, setForm]             = useState<PensionForm>({ ...BLANK_FORM })
  const [editingId, setEditingId]   = useState<number | null>(null)
  const [saving, setSaving]         = useState(false)
  const [formError, setFormError]   = useState<string | null>(null)

  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null)
  const [deleting, setDeleting]           = useState(false)

  // Impact preview
  const [impact, setImpact]         = useState<OptimiseResponse | null>(null)
  const [impactLoading, setImpactLoading] = useState(false)
  const [impactError, setImpactError]     = useState<string | null>(null)

  // ── Fetch list ─────────────────────────────────────────────────────────────

  const fetchPensions = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ pensions: DbPension[] }>('/api/profile/db-pensions')
      setPensions(res.data.pensions || [])
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load DB pensions')
    } finally {
      setLoading(false)
    }
  }

  // ── Impact preview ─────────────────────────────────────────────────────────

  const fetchImpact = async () => {
    setImpactLoading(true)
    setImpactError(null)
    try {
      const res = await api.post<OptimiseResponse>('/api/drawdown/optimise', OPTIMISE_DEFAULTS)
      setImpact(res.data)
    } catch (err: any) {
      setImpactError(err?.message ?? 'Failed to load impact preview')
    } finally {
      setImpactLoading(false)
    }
  }

  useEffect(() => {
    fetchPensions()
    fetchImpact()
  }, [])

  // ── Add / Edit ─────────────────────────────────────────────────────────────

  const startEdit = (p: DbPension) => {
    setEditingId(p.id)
    setForm({
      owner:              p.owner,
      name:               p.name,
      annual_amount_gbp:  p.annual_amount_gbp,
      start_age:          p.start_age,
      indexation_pct:     p.indexation_pct,
      spouse_fraction:    p.spouse_fraction,
      notes:              p.notes ?? '',
    })
    setFormError(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setForm({ ...BLANK_FORM })
    setFormError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    if (!form.name.trim()) {
      setFormError('Name is required')
      return
    }
    const amount = Number(form.annual_amount_gbp)
    if (!amount || amount <= 0) {
      setFormError('Annual amount must be a positive number')
      return
    }

    setSaving(true)
    try {
      const body: Record<string, unknown> = {
        owner:             form.owner,
        name:              form.name.trim(),
        annual_amount_gbp: amount,
        start_age:         Number(form.start_age),
        indexation_pct:    Number(form.indexation_pct),
        spouse_fraction:   Number(form.spouse_fraction),
        notes:             form.notes?.trim() || null,
      }
      if (editingId !== null) body.id = editingId

      await api.post('/api/profile/db-pensions', body)
      await fetchPensions()
      await fetchImpact()
      cancelEdit()
    } catch (err: any) {
      setFormError(err?.message ?? 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async (id: number) => {
    setDeleting(true)
    try {
      await api.delete(`/api/profile/db-pensions/${id}`)
      await fetchPensions()
      await fetchImpact()
    } catch (err: any) {
      setError(err?.message ?? 'Delete failed')
    } finally {
      setDeleting(false)
      setConfirmDelete(null)
    }
  }

  // ── Derived ────────────────────────────────────────────────────────────────

  const annualTotal = pensions.reduce((s, p) => s + p.annual_amount_gbp, 0)

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 animate-fade-in">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <PiggyBank size={20} className="text-blue-light" aria-hidden />
            Defined Benefit / Guaranteed Pensions
          </h1>
          <p className="text-sm text-text-secondary">
            Bank of England, employer schemes, annuities — anything that pays a fixed amount for life.
            These eat into your personal allowance and basic-rate band before SIPP drawdown starts.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { fetchPensions(); fetchImpact() }}
          className="btn-secondary gap-2 text-xs"
          disabled={loading}
        >
          <RefreshCw size={13} aria-hidden className={clsx(loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      <PensionsTabs />

      {/* Info card */}
      <div className="card p-4 bg-blue/5 border-blue/20 flex items-start gap-3">
        <Info size={16} className="text-blue-light shrink-0 mt-0.5" aria-hidden />
        <div className="space-y-1 text-sm text-text-secondary">
          <p>
            <span className="text-text-primary font-semibold">Why these matter for drawdown:</span>{' '}
            DB pension income is taxable from day one. It fills your Personal Allowance (£12,570)
            and basic-rate band (£50,270) before any SIPP withdrawal is taxed — leaving less room
            for tax-free or low-tax SIPP drawdown each year.
          </p>
          {pensions.length > 0 && (
            <p className="font-medium text-text-primary">
              Combined guaranteed income:{' '}
              <span className="text-blue-light">{fmt(annualTotal)}/yr</span>
              {' '}across {pensions.length} pension{pensions.length !== 1 ? 's' : ''}.
            </p>
          )}
        </div>
      </div>

      {/* Global error */}
      {error && (
        <div className="card p-4 flex items-center gap-3 border-rose/30 bg-rose/5">
          <AlertTriangle size={16} className="text-rose-light shrink-0" aria-hidden />
          <p className="text-sm text-rose-light flex-1">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-xs text-text-muted hover:text-text-secondary"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Add / Edit form ── */}
      <section>
        <h2 className="panel-title mb-3 flex items-center gap-2">
          {editingId !== null
            ? <><Edit2 size={14} className="text-amber" aria-hidden /> Edit Pension</>
            : <><Plus size={14} className="text-emerald" aria-hidden /> Add Pension</>
          }
        </h2>

        <form onSubmit={handleSubmit} className="card p-4 space-y-4">
          {formError && (
            <div className="flex items-center gap-2 text-sm text-rose-light bg-rose/5 border border-rose/20 rounded-lg px-3 py-2">
              <AlertTriangle size={13} aria-hidden />
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

            {/* Owner */}
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1" htmlFor="db-owner">
                Owner
              </label>
              <select
                id="db-owner"
                value={form.owner}
                onChange={e => setForm(f => ({ ...f, owner: e.target.value }))}
                className="input w-full"
              >
                <option value="Simon">Simon</option>
                <option value="Unman">Unman</option>
                <option value="Joint">Joint</option>
              </select>
            </div>

            {/* Name */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-text-secondary mb-1" htmlFor="db-name">
                Pension name
              </label>
              <input
                id="db-name"
                type="text"
                placeholder="e.g. Bank of England DB Pension"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="input w-full"
                required
              />
            </div>

            {/* Annual amount */}
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1" htmlFor="db-amount">
                Annual amount (£)
              </label>
              <input
                id="db-amount"
                type="number"
                min={0}
                step={100}
                placeholder="e.g. 25000"
                value={form.annual_amount_gbp}
                onChange={e => setForm(f => ({ ...f, annual_amount_gbp: e.target.value }))}
                className="input w-full"
                required
              />
            </div>

            {/* Start age */}
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1" htmlFor="db-start-age">
                Start age (scheme retirement age)
              </label>
              <input
                id="db-start-age"
                type="number"
                min={50}
                max={75}
                step={1}
                value={form.start_age}
                onChange={e => setForm(f => ({ ...f, start_age: Number(e.target.value) }))}
                className="input w-full"
              />
            </div>

            {/* Indexation */}
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1" htmlFor="db-indexation">
                Indexation % (CPI cap)
              </label>
              <input
                id="db-indexation"
                type="number"
                min={0}
                max={10}
                step={0.1}
                value={form.indexation_pct}
                onChange={e => setForm(f => ({ ...f, indexation_pct: Number(e.target.value) }))}
                className="input w-full"
              />
            </div>

            {/* Spouse fraction */}
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1" htmlFor="db-spouse">
                Spouse survivor fraction (0–1)
              </label>
              <input
                id="db-spouse"
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={form.spouse_fraction}
                onChange={e => setForm(f => ({ ...f, spouse_fraction: Number(e.target.value) }))}
                className="input w-full"
              />
            </div>

            {/* Notes */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-text-secondary mb-1" htmlFor="db-notes">
                Notes <span className="font-normal text-text-muted">(optional)</span>
              </label>
              <textarea
                id="db-notes"
                rows={2}
                placeholder="e.g. Accrued 25 years service, deferred from 2024"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="input w-full resize-none"
              />
            </div>

          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              className="btn-primary gap-2 text-sm"
              disabled={saving}
            >
              {saving
                ? <RefreshCw size={13} className="animate-spin" aria-hidden />
                : editingId !== null
                  ? <Save size={13} aria-hidden />
                  : <Plus size={13} aria-hidden />
              }
              {saving ? 'Saving…' : editingId !== null ? 'Save changes' : 'Add pension'}
            </button>
            {editingId !== null && (
              <button
                type="button"
                onClick={cancelEdit}
                className="btn-secondary text-sm"
                disabled={saving}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      {/* ── Pension list ── */}
      <section>
        <h2 className="panel-title mb-3 flex items-center gap-2">
          <PiggyBank size={14} className="text-text-muted" aria-hidden />
          Existing Pensions
          {pensions.length > 0 && (
            <span className="text-xs text-text-muted font-normal">({pensions.length})</span>
          )}
        </h2>

        {loading && !pensions.length ? (
          <div className="space-y-3">
            {[0, 1].map(i => (
              <div key={i} className="h-24 skeleton" />
            ))}
          </div>
        ) : pensions.length === 0 ? (
          <div className="card p-6 text-center text-sm text-text-muted">
            No DB pensions added yet. Use the form above to add one.
          </div>
        ) : (
          <div className="space-y-3">
            {pensions.map(p => (
              <div
                key={p.id}
                className={clsx(
                  'card p-4 flex flex-wrap items-start gap-4',
                  editingId === p.id && 'border-amber/40 bg-amber/5',
                )}
              >
                {/* Left: badge + name */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={clsx('badge text-[10px]', OWNER_BADGE[p.owner] ?? 'badge-blue')}>
                      {p.owner}
                    </span>
                    <span className="text-sm font-semibold text-text-primary truncate">{p.name}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-secondary">
                    <span>
                      <span className="text-text-muted">Amount:</span>{' '}
                      <span className="font-mono font-semibold text-text-primary">
                        {fmt(p.annual_amount_gbp)}/yr
                      </span>
                    </span>
                    <span>
                      <span className="text-text-muted">Starts at:</span>{' '}
                      age {p.start_age}
                    </span>
                    <span>
                      <span className="text-text-muted">Indexation:</span>{' '}
                      {p.indexation_pct}%
                    </span>
                    <span>
                      <span className="text-text-muted">Survivor:</span>{' '}
                      {(p.spouse_fraction * 100).toFixed(0)}%
                    </span>
                  </div>
                  {p.notes && (
                    <p className="mt-1 text-xs text-text-muted italic">{p.notes}</p>
                  )}
                </div>

                {/* Right: actions */}
                <div className="flex items-center gap-2 shrink-0">
                  {confirmDelete === p.id ? (
                    <>
                      <span className="text-xs text-rose-light">Delete?</span>
                      <button
                        type="button"
                        onClick={() => handleDelete(p.id)}
                        disabled={deleting}
                        className="btn-primary text-xs py-1 bg-rose/80 hover:bg-rose border-rose/50 gap-1"
                      >
                        {deleting
                          ? <RefreshCw size={11} className="animate-spin" aria-hidden />
                          : <Trash2 size={11} aria-hidden />
                        }
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(null)}
                        disabled={deleting}
                        className="btn-secondary text-xs py-1"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => startEdit(p)}
                        className="btn-secondary text-xs py-1 gap-1"
                        aria-label={`Edit ${p.name}`}
                      >
                        <Edit2 size={11} aria-hidden />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(p.id)}
                        className="btn-secondary text-xs py-1 gap-1 hover:border-rose/40 hover:text-rose-light"
                        aria-label={`Delete ${p.name}`}
                      >
                        <Trash2 size={11} aria-hidden />
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Impact preview ── */}
      <section>
        <h2 className="panel-title mb-3 flex items-center gap-2">
          <Info size={14} className="text-blue-light" aria-hidden />
          Impact Preview — First Year of Retirement
        </h2>

        {impactLoading ? (
          <div className="h-32 skeleton" />
        ) : impactError ? (
          <div className="card p-4 flex items-center gap-3 border-rose/30 bg-rose/5">
            <AlertTriangle size={15} className="text-rose-light shrink-0" aria-hidden />
            <p className="text-sm text-rose-light flex-1">{impactError}</p>
            <button
              type="button"
              onClick={fetchImpact}
              className="btn-secondary text-xs"
            >
              Retry
            </button>
          </div>
        ) : impact && !impact.first_retire_year ? (
          /* The optimiser returns first_retire_year: null when no drawdown
             year falls inside the projection window (retirement far away) —
             reading .sources off it used to crash the page. */
          <div className="card p-4">
            <p className="text-sm text-text-secondary">
              Retirement falls outside the projection window, so there&apos;s no first
              drawdown year to break down yet. Bring the retirement age closer (or
              extend the projection) to see the DB-pension impact.
            </p>
          </div>
        ) : impact?.first_retire_year ? (
          ((fry) => (
          <div className="card p-4 space-y-4">
            {/* DB pension line */}
            <div className="flex items-start gap-3">
              <PiggyBank size={16} className="text-blue-light shrink-0 mt-0.5" aria-hidden />
              <div className="flex-1 space-y-1">
                <p className="text-sm text-text-primary">
                  <span className="font-semibold">
                    {fmt(fry.sources.db_pension_gbp)}/yr
                  </span>{' '}
                  of DB pension income in year 1 of retirement.
                </p>
                <p className="text-xs text-text-secondary">
                  This {fmt(fry.sources.db_pension_gbp)}/yr of guaranteed income
                  arrives as taxable income and is counted first against your Personal Allowance
                  (£12,570) and basic-rate band before any SIPP drawdown is added — directly
                  reducing how much you can draw from your SIPP at the basic rate.
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-text-muted mb-0.5">Effective tax rate</p>
                <p className="stat-hero text-amber">
                  {fry.effective_tax_rate_pct.toFixed(1)}%
                </p>
              </div>
            </div>

            {/* Source blend summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 stagger-children">
              {(
                [
                  { key: 'db_pension_gbp',       label: 'DB Pension',    colour: 'text-blue-light' },
                  { key: 'state_pension_gbp',     label: 'State Pension', colour: 'text-emerald-light' },
                  { key: 'sipp_basic_rate_gbp',   label: 'SIPP (basic)',  colour: 'text-amber' },
                  { key: 'isa_gbp',               label: 'ISA',           colour: 'text-text-primary' },
                ] as const
              ).map(({ key, label, colour }) => (
                <div key={key} className="stat-card">
                  <p className="stat-label">{label}</p>
                  <p className={clsx('text-base font-bold font-mono', colour)}>
                    {fmt((fry.sources as unknown as Record<string, number>)[key] ?? 0)}
                  </p>
                  <p className="text-[10px] text-text-muted">per year</p>
                </div>
              ))}
            </div>

            <p className="text-[11px] text-text-muted">
              Modelled at retirement age 60, target £200,000 gross/yr, using current portfolio balances.
              Refresh after adding or editing pensions to see updated figures.
            </p>
          </div>
          ))(impact.first_retire_year)
        ) : null}
      </section>

    </div>
  )
}
